import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { JSONLD, Generic } from 'react-structured-data'
import { useStaticQuery, graphql } from 'gatsby';
import { buildImageObj, imageUrlFor } from './sanity/image-url';
import defaultMetaImage from '../images/meta/kp-meta-image.png';

function SEO({ description, lang, meta, keywords, title, image, bodyClasses, bodyId, defaultMicroData, microDataType }) {

  const data = useStaticQuery(graphql`
  query SEOQuery {
    site: sanitySiteSettings(_id: {eq: "siteSettings"}) {
      title
      description
      keywords
    }
  }
`);

  const metaDescription = description || (data.site && data.site.description) || '';
  const siteTitle = (data.site && data.site.title) || '';
  const metaImage = (image && image.asset) ? imageUrlFor(buildImageObj(image)).width(1200).url() : `https://www.kleinerperkins.com${defaultMetaImage}`;
  let bodyAttributes = {};
  bodyClasses ? bodyAttributes.class = bodyClasses : null;
  bodyId ? bodyAttributes.id = bodyId : null;

  return (
    <>
      <Helmet
        htmlAttributes={{ lang }}
        title={title}
        titleTemplate={title === siteTitle ? '%s' : `%s | ${siteTitle}`}
        bodyAttributes={bodyAttributes}
        meta={[
          {
            name: 'description',
            content: metaDescription
          },
          {
            property: 'og:title',
            content: title
          },
          {
            property: 'og:description',
            content: metaDescription
          },
          {
            property: 'og:image',
            content: metaImage
          },
          {
            property: 'og:url',
            content: typeof window !== 'undefined' && window.location.href
          },
          {
            property: 'og:type',
            content: 'website'
          },
          {
            name: 'twitter:card',
            content: 'summary'
          },
          {
            name: 'twitter:creator',
            content: title
          },
          {
            name: 'twitter:title',
            content: title
          },
          {
            name: 'twitter:description',
            content: metaDescription
          },
          {
            name: 'twitter:image',
            content: metaImage
          }
        ]
          .concat(
            keywords && keywords.length > 0
              ? {
                name: 'keywords',
                content: keywords.join(', ')
              }
              : []
          )
          .concat(meta)}
      />
      
      {/* GENERAL PAGES */}
      {(defaultMicroData && !microDataType) && (
        <JSONLD>
          <Generic type="webpage" jsonldtype="WebPage" schema={{
            name: title, 
            description: metaDescription,
            url: typeof window !== 'undefined' && window.location.href,
          }}/>
        </JSONLD>
      )}

      {/* HOMEPAGE */}
      {(defaultMicroData && microDataType === 'home') && (
        <JSONLD>
          <Generic type="corporation" jsonldtype="Corporation" schema={{
            name: "Kleiner Perkins", 
            url: typeof window !== 'undefined' && window.location.href,
            logo: "https://cdn.sanity.io/images/ti7si9cx/production/f8f0cdc352f5602a942f4f86e6bb6c18860a74f8-1024x512.png?rect=55,0,914,512&w=500&h=280",
            image: "https://cdn.sanity.io/images/ti7si9cx/production/f8f0cdc352f5602a942f4f86e6bb6c18860a74f8-1024x512.png?rect=55,0,914,512&w=500&h=280",
            sameAs: [
              "https://web.facebook.com/kleinerperkinsprofile",
              "https://twitter.com/kleinerperkins",
              "https://www.instagram.com/kleiner_perkins/",
              "https://en.wikipedia.org/wiki/Kleiner_Perkins",
              "https://www.kleinerperkins.com/",
              "https://www.linkedin.com/company/kleinerperkins/",
              "https://www.youtube.com/user/KPCBchannel",
              "https://www.crunchbase.com/organization/kleiner-perkins-caufield-byers"
            ]
          }} />
        </JSONLD>
      )}
    </>
  );
}

SEO.defaultProps = {
  lang: 'en',
  meta: [],
  keywords: []
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired
};

export default SEO;
